/* ==========================================================================
   Cart Drawer — CartAPI + <cart-drawer> Web Component
   --------------------------------------------------------------------------
   Server-authoritative cart drawer built on Shopify's Section Rendering API.
   Replaces the legacy liquid-ajax-cart "sidecart". Drop-in compatible: it
   exposes a window.liquidAjaxCart shim and watches the legacy
   `js-ajax-cart-open` body class so existing add-to-cart call sites
   (theme.js, hue-integration, subscription-modal, bundle-builder) keep
   working unmodified.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var OPEN_CLASS = 'js-ajax-cart-open';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // -------------------------------------------------------------------------
  // CartAPI — thin wrapper over /cart/*.js, always requesting our section back
  // -------------------------------------------------------------------------

  function fetchWithRetry(url, options, maxRetries) {
    maxRetries = maxRetries == null ? 3 : maxRetries;
    var attempt = 0;
    function run() {
      return fetch(url, options).then(function (response) {
        if (response.status === 429 && attempt < maxRetries) {
          attempt++;
          var delay = 1000 * Math.pow(2, attempt - 1); // 1s, 2s, 4s
          return new Promise(function (r) { setTimeout(r, delay); }).then(run);
        }
        return response;
      });
    }
    return run();
  }

  var CartAPI = {
    add: function (body) {
      var opts = { method: 'POST' };
      if (body instanceof FormData) {
        body.append('sections', SECTION_ID);
        opts.body = body;
      } else {
        opts.headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        opts.body = JSON.stringify(Object.assign({ sections: SECTION_ID }, body));
      }
      return fetchWithRetry(root + 'cart/add.js', opts).then(function (res) {
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (err) {
            var e = new Error(err.description || ('Add failed: ' + res.status));
            e.shopifyError = err;
            throw e;
          });
        }
        return res.json();
      });
    },

    change: function (key, quantity, sellingPlan) {
      var payload = { id: String(key), quantity: quantity, sections: SECTION_ID };
      if (sellingPlan !== undefined) payload.selling_plan = sellingPlan;
      return fetchWithRetry(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        if (!res.ok) throw new Error('Change failed: ' + res.status);
        return res.json();
      });
    },

    update: function (updates) {
      return fetchWithRetry(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: SECTION_ID }, updates))
      }).then(function (res) {
        if (!res.ok) throw new Error('Update failed: ' + res.status);
        return res.json();
      });
    },

    // Plain cart object (no Section Rendering — /cart.js ignores ?sections).
    // Used to seed recommendations (needs items[].product_id).
    get: function () {
      return fetchWithRetry(root + 'cart.js', {
        headers: { Accept: 'application/json' }
      }).then(function (res) {
        if (!res.ok) throw new Error('Get failed: ' + res.status);
        return res.json();
      });
    },

    // No-op mutation that reliably returns freshly-rendered sections.
    // Used to re-render the drawer after an external add (Hue, sub modal, etc.).
    refresh: function () {
      return fetchWithRetry(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: {}, sections: SECTION_ID })
      }).then(function (res) {
        if (!res.ok) throw new Error('Refresh failed: ' + res.status);
        return res.json();
      });
    }
  };

  window.CartAPI = CartAPI;

  // -------------------------------------------------------------------------
  // <cart-drawer> Web Component
  // -------------------------------------------------------------------------

  function CartDrawerElement() {
    return Reflect.construct(HTMLElement, [], CartDrawerElement);
  }
  CartDrawerElement.prototype = Object.create(HTMLElement.prototype);
  CartDrawerElement.prototype.constructor = CartDrawerElement;
  Object.setPrototypeOf(CartDrawerElement, HTMLElement);

  CartDrawerElement.prototype.connectedCallback = function () {
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.panel = this.querySelector('.cart-drawer__panel');
    this.body = this.querySelector('.cart-drawer__body');
    this.closeBtn = this.querySelector('.cart-drawer__close');
    this.countEl = this.querySelector('[data-cart-count]');

    this.debounceTimers = {};
    this.triggerElement = null;
    this._queue = [];
    this._processing = false;

    // Bind once for add/remove symmetry
    this._onClick = this.onClick.bind(this);
    this._onChange = this.onChange.bind(this);
    this._onKeydown = this.onKeydown.bind(this);
    this._onTriggerClick = this.onTriggerClick.bind(this);
    this._onFormSubmit = this.onFormSubmit.bind(this);
    this._onAtcClick = this.onAtcClick.bind(this);
    this._atcButtons = [];

    document.addEventListener('click', this._onClick);
    document.addEventListener('change', this._onChange);
    document.addEventListener('keydown', this._onKeydown);
    // Capture phase so we run before the legacy handlers
    document.addEventListener('click', this._onTriggerClick, true);
    // Capture phase so we own the add and block theme.js's ProductForm handler
    document.addEventListener('submit', this._onFormSubmit, true);
    // Capture phase — show a loading spinner on any sitewide ATC button on click
    document.addEventListener('click', this._onAtcClick, true);

    // Mirror the legacy body class: any existing caller that adds
    // `js-ajax-cart-open` should open this drawer; removing it closes.
    this._bodyObserver = new MutationObserver(this.onBodyClassChange.bind(this));
    this._bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    if (document.body.classList.contains(OPEN_CLASS)) this.open();

    this.setupShim();

    // Warm the recs cache on load so the first drawer open is instant.
    this.prefetchRecs(this.currentSeedId());
  };

  CartDrawerElement.prototype.disconnectedCallback = function () {
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('change', this._onChange);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onTriggerClick, true);
    document.removeEventListener('submit', this._onFormSubmit, true);
    document.removeEventListener('click', this._onAtcClick, true);
    if (this._bodyObserver) this._bodyObserver.disconnect();
  };

  // ----- Sitewide ATC loading spinner -----
  // Shows a spinner on any add-to-cart button (PDP form, quick-add, carousels,
  // bundle builder, subscription modal) until the cart re-renders / drawer opens.

  CartDrawerElement.prototype.onAtcClick = function (e) {
    var btn = e.target.closest('.ajax-add-to-cart-btn, .cart__checkout, #subscribe_btn');
    if (!btn || btn.disabled) return;
    this.markAtcLoading(btn);
    this.prefetchRecs(this.currentSeedId());  // overlap recs fetch with the add
  };

  CartDrawerElement.prototype.markAtcLoading = function (btn) {
    if (!btn || btn.classList.contains('atc-loading')) return;
    var self = this;
    var rgb = restingBg(btn);
    var spinner = '#1a1a1a';
    if (rgb) {
      var lum = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
      spinner = lum < 140 ? '#ffffff' : '#1a1a1a';
      // Lock the resting background for the duration of loading so neither the
      // sticky :hover on touch (white) nor the cursor leaving on desktop (black)
      // can change it — keeps the bg consistent across mobile/desktop.
      btn.style.setProperty('background-color', 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')', 'important');
    }
    btn.style.setProperty('--atc-spinner', spinner);
    btn.classList.add('atc-loading');
    if (!this._atcButtons) this._atcButtons = [];
    if (this._atcButtons.indexOf(btn) === -1) this._atcButtons.push(btn);
    clearTimeout(btn._atcTimer);
    btn._atcTimer = setTimeout(function () { self.clearAtcLoading(btn); }, 6000); // fallback
  };

  CartDrawerElement.prototype.clearAtcLoading = function (btn) {
    if (!btn) return;
    clearTimeout(btn._atcTimer);
    btn.classList.remove('atc-loading');
    btn.style.removeProperty('--atc-spinner');
    btn.style.removeProperty('background-color');
  };

  CartDrawerElement.prototype.clearAllAtcLoading = function () {
    var self = this;
    (this._atcButtons || []).forEach(function (b) { self.clearAtcLoading(b); });
    this._atcButtons = [];
    // sweep any stragglers (e.g. buttons re-rendered by other components)
    document.querySelectorAll('.atc-loading').forEach(function (b) { self.clearAtcLoading(b); });
  };

  // ----- Action queue: mutations run one at a time, in order -----

  CartDrawerElement.prototype.enqueue = function (fn) {
    var self = this;
    this._queue.push(fn);
    if (this._processing) return;
    this._processing = true;
    (function next() {
      if (!self._queue.length) {
        self._processing = false;
        self.flushPendingOpen();  // catch-all: never strand a deferred open
        return;
      }
      var task = self._queue.shift();
      Promise.resolve()
        .then(task)
        .catch(function (err) { console.error('Cart queue task failed:', err); })
        .then(next);
    })();
  };

  // Open that was requested mid-render (e.g. legacy callers add the body class
  // immediately after firing cartRequestUpdate) — perform it now.
  CartDrawerElement.prototype.flushPendingOpen = function () {
    if (!this._openRequested) return;
    this._openRequested = false;
    this.open(false, true);
  };

  // ----- Event handlers -----

  CartDrawerElement.prototype.onTriggerClick = function (e) {
    var trigger = e.target.closest('[data-ajax-cart-toggle-class-button="' + OPEN_CLASS + '"]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    if (this.classList.contains('is-open')) this.close();
    else this.open();
  };

  // Capture-phase add-to-cart: we own product forms so the drawer always
  // renders from the add.js response (which honors `sections`) and we get the
  // product data. Blocks theme.js's legacy ProductForm handler + "added to
  // cart" modal.
  CartDrawerElement.prototype.onFormSubmit = function (e) {
    var form = e.target;
    if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
    // Bundle builder owns its own forms.
    if (form.closest('bundle-builder')) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    var submitBtn = e.submitter || form.querySelector('[type="submit"]');
    this.addToCart(form, submitBtn);
  };

  CartDrawerElement.prototype.addToCart = function (form, submitBtn) {
    var self = this;
    if (submitBtn && submitBtn.disabled) return;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('is-loading');
      this.markAtcLoading(submitBtn);
    }
    var reenable = function () {
      var btn = (submitBtn && submitBtn.isConnected) ? submitBtn : form.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
      }
    };

    // Product title (for SR announce + analytics)
    var titleEl = form.closest('[data-product-title]')
      || document.querySelector('h1.product-title, .product-area .product_name, .product__title');
    var title = titleEl ? titleEl.textContent.trim() : '';

    this.setBusy(true);
    this.prefetchRecs(this.currentSeedId());  // overlap recs fetch with the add
    this.enqueue(function () {
      var formData = new FormData(form);
      // A form may carry multiple id inputs (ATC + Shop Pay). FormData.get
      // returns the first per spec; re-append it as the only id.
      var correctId = formData.get('id');
      if (correctId != null) { formData.delete('id'); formData.append('id', correctId); }

      return CartAPI.add(formData).then(function (data) {
        return self.renderFromResponse(data, 'add', title).then(function () {
          self.open();
          setTimeout(reenable, 400);
        });
      }).catch(function (err) {
        console.error('Add to cart error:', err);
        var wrapper = form.querySelector('.product-status-message, .product-form__error-message-wrapper');
        if (wrapper) {
          wrapper.textContent = (err && err.message) || 'Could not add item to cart';
          wrapper.removeAttribute('hidden');
          wrapper.style.display = '';
        }
        reenable();
      });
    });
  };

  CartDrawerElement.prototype.onClick = function (e) {
    // Overlay closes
    if (e.target === this.overlay) { this.close(); return; }

    var actionEl = e.target.closest('[data-action]');
    if (!actionEl || !(actionEl.closest('cart-drawer') || actionEl.closest('.cart-page'))) return;

    var action = actionEl.dataset.action;
    var key = actionEl.dataset.key;

    if (action === 'close') {
      this.close();
    } else if (action === 'decrease' && key) {
      var dq = (parseInt(actionEl.dataset.qty, 10) || 0) - 1;
      this.setBusy(true);
      this.debouncedChange(key, Math.max(0, dq));
    } else if (action === 'increase' && key) {
      var iq = (parseInt(actionEl.dataset.qty, 10) || 0) + 1;
      this.setBusy(true);
      this.debouncedChange(key, iq);
    } else if (action === 'remove' && key) {
      this.setBusy(true);
      this.changeQuantity(key, 0, 'remove');
    } else if (action === 'subscribe' && key) {
      var planId = actionEl.dataset.sellingPlanId;
      var subQty = Math.max(1, parseInt(actionEl.dataset.quantity, 10) || 1);
      if (planId) { this.setBusy(true); this.changeSellingPlan(key, subQty, planId); }
    } else if (action === 'remove-bundle') {
      this.setBusy(true);
      this.removeBundle(actionEl);
    } else if (action === 'add-rec') {
      this.addRecItem(actionEl);
    }
  };

  CartDrawerElement.prototype.onChange = function (e) {
    var el = e.target;
    if (!el || !(el.closest('cart-drawer') || el.closest('.cart-page'))) return;

    // Subscription frequency / selling-plan change
    if (el.matches && el.matches('select[data-action="change-frequency"]')) {
      var fk = el.dataset.key;
      var planId = el.value;
      var fq = Math.max(1, parseInt(el.dataset.quantity, 10) || 1);
      if (fk && planId) { this.setBusy(true); this.changeSellingPlan(fk, fq, planId); }
      return;
    }

    // Quantity input
    if (!el.hasAttribute || !el.hasAttribute('data-quantity-input')) return;
    var key = el.dataset.key;
    if (!key) return;
    var qty = Math.max(0, parseInt(el.value, 10) || 0);
    this.setBusy(true);
    this.debouncedChange(key, qty);
  };

  CartDrawerElement.prototype.changeSellingPlan = function (key, qty, planId) {
    var self = this;
    var itemEl = document.querySelector('.cart-item[data-key="' + key + '"]');
    var nameEl = itemEl ? itemEl.querySelector('.cart-item__name') : null;
    var title = nameEl ? nameEl.textContent.trim() : '';
    this.prefetchRecs(this.currentSeedId());
    this.enqueue(function () {
      return CartAPI.change(key, qty, parseInt(planId, 10)).then(function (data) {
        return self.renderFromResponse(data, 'update', title);
      }).catch(function (err) {
        console.error('Frequency change error:', err);
        self.setBusy(false);
      });
    });
  };

  CartDrawerElement.prototype.onKeydown = function (e) {
    if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
  };

  CartDrawerElement.prototype.onBodyClassChange = function () {
    var open = document.body.classList.contains(OPEN_CLASS);
    if (open && !this.classList.contains('is-open')) this.open(true);
    else if (!open && this.classList.contains('is-open')) this.close(true);
  };

  // ----- Open / close -----

  CartDrawerElement.prototype.open = function (fromBodyClass, force) {
    if (this.classList.contains('is-open')) return;
    // Legacy callers (quick-add, Hue, subscription modal) add the body class
    // immediately after firing the cart refresh — before the re-render lands.
    // Defer the visual open until the fully-rendered body is in place;
    // renderFromResponse / the queue drain flush it.
    if (fromBodyClass && !force && (this._processing || this.classList.contains('is-busy'))) {
      if (!this._openRequested) this.triggerElement = document.activeElement;
      this._openRequested = true;
      return;
    }
    this._openRequested = false;
    this.triggerElement = this.triggerElement || document.activeElement;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (!fromBodyClass) document.body.classList.add(OPEN_CLASS);
    var self = this;
    requestAnimationFrame(function () { if (self.closeBtn) self.closeBtn.focus(); });
    this.ensureRecsLoaded();
  };

  CartDrawerElement.prototype.close = function (fromBodyClass) {
    this._openRequested = false;  // cancel any deferred open
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    if (!fromBodyClass) document.body.classList.remove(OPEN_CLASS);
    if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
      this.triggerElement.focus();
    }
    this.triggerElement = null;
  };

  CartDrawerElement.prototype.setBusy = function (busy) {
    this.classList.toggle('is-busy', !!busy);
    var page = document.querySelector('.cart-page');
    if (page) page.classList.toggle('is-busy', !!busy);
  };

  // ----- Mutations -----

  CartDrawerElement.prototype.debouncedChange = function (key, qty) {
    var self = this;
    clearTimeout(this.debounceTimers[key]);
    this.debounceTimers[key] = setTimeout(function () {
      self.changeQuantity(key, qty, qty === 0 ? 'remove' : 'update');
    }, 450);
  };

  CartDrawerElement.prototype.changeQuantity = function (key, qty, action) {
    var self = this;
    var itemEl = document.querySelector('.cart-item[data-key="' + key + '"]');
    var nameEl = itemEl ? itemEl.querySelector('.cart-item__name') : null;
    var title = nameEl ? nameEl.textContent.trim() : '';
    this.prefetchRecs(this.currentSeedId());
    this.enqueue(function () {
      return CartAPI.change(key, qty).then(function (data) {
        return self.renderFromResponse(data, action, title);
      }).catch(function (err) {
        console.error('Change error:', err);
        self.setBusy(false);
      });
    });
  };

  // Remove every line in one bundle card in a single update.
  CartDrawerElement.prototype.removeBundle = function (actionEl) {
    var self = this;
    var card = actionEl.closest('.cart-bundle');
    if (!card) { this.setBusy(false); return; }
    var updates = {};
    var keys = card.querySelectorAll('.cart-bundle__item[data-key]');
    Array.prototype.forEach.call(keys, function (el) {
      updates[el.getAttribute('data-key')] = 0;
    });
    if (!Object.keys(updates).length) { this.setBusy(false); return; }
    this.prefetchRecs(this.currentSeedId());
    this.enqueue(function () {
      return CartAPI.update({ updates: updates }).then(function (data) {
        return self.renderFromResponse(data, 'remove', 'Bundle');
      }).catch(function (err) {
        console.error('Remove bundle error:', err);
        self.setBusy(false);
      });
    });
  };

  CartDrawerElement.prototype.addRecItem = function (btn) {
    var self = this;
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    this.setBusy(true);
    this.prefetchRecs(this.currentSeedId());
    this.enqueue(function () {
      return CartAPI.add({ items: [{ id: variantId, quantity: 1 }] }).then(function (data) {
        return self.renderFromResponse(data, 'add', '').then(function () { self.open(); });
      }).catch(function (err) {
        console.error('Add rec error:', err);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        self.setBusy(false);
      });
    });
  };

  // ----- Rendering -----

  // Extracts the freshly-rendered `.cart-drawer__body` inner HTML from a
  // Section Rendering API payload and swaps it into the live shell.
  // Buffered render: build the new body off-DOM, inject recommendations into it
  // BEFORE revealing, then swap the complete drawer in one shot. Returns a
  // promise that resolves once the drawer is fully rendered (the busy/spinner
  // state is held until then). No empty-slot-then-pop-in.
  CartDrawerElement.prototype.renderFromResponse = function (data, action, title) {
    var self = this;
    var newBody = null;
    var sectionHTML = data && data.sections && data.sections[SECTION_ID];
    if (sectionHTML) {
      var temp = document.createElement('div');
      temp.innerHTML = sectionHTML;
      newBody = temp.querySelector('.cart-drawer__body');
    }

    var recsReady = Promise.resolve();
    if (newBody && this.recsEnabled()) {
      var recsSlot = newBody.querySelector('.cart-drawer__recs');
      var onPdp = document.body.classList.contains('template-product') &&
        window.meta && window.meta.product && window.meta.product.id;
      var firstItem = newBody.querySelector('.cart-item[data-product-id]');
      var seedId = onPdp ? String(window.meta.product.id)
        : (firstItem ? firstItem.getAttribute('data-product-id') : null);

      if (recsSlot && seedId) {
        var heading = recsSlot.dataset.recsHeading || 'Your Hair might like this too';
        var recsPromise = this.prefetchRecs(seedId);
        // Timeout guard: never block the drawer on a slow recs fetch.
        var timeout = new Promise(function (res) { setTimeout(function () { res(null); }, 2000); });
        recsReady = Promise.race([recsPromise, timeout]).then(function (products) {
          if (products === null) {
            // Slow: reveal now, inject late into the live slot when it arrives.
            recsPromise.then(function (late) {
              var liveSlot = self.body && self.body.querySelector('.cart-drawer__recs');
              if (liveSlot && !liveSlot.innerHTML && late && late.length) {
                liveSlot.innerHTML = self.buildRecsHTML(late, self, heading);
              }
            });
            return;
          }
          if (products.length) recsSlot.innerHTML = self.buildRecsHTML(products, newBody, heading);
        });
      }
    }

    return recsReady.then(function () {
      if (newBody && self.body) self.body.innerHTML = newBody.innerHTML;

      // Item count (from re-rendered wrapper, falls back to the cart payload)
      var inner = self.body ? self.body.querySelector('#cart-drawer-inner') : null;
      var count = inner ? parseInt(inner.dataset.cartItemCount, 10) : null;
      if (count == null && typeof data.item_count === 'number') count = data.item_count;
      if (count == null) count = 0;
      self.updateBadges(count);

      self.syncCartPage(newBody, count);

      self.setBusy(false);
      self.clearAllAtcLoading();
      self.flushPendingOpen();  // open now that the full body is rendered
      self.triggerCurrencyConverter();
      self.announce(action, title);
      self.pushAnalytics(action, title);
    });
  };

  // Keep the full-page /cart in sync with the same drawer-section HTML.
  CartDrawerElement.prototype.syncCartPage = function (newBody, count) {
    var page = document.querySelector('.cart-page');
    if (!page) return;
    // Last item removed → let the server render the proper empty cart page.
    if (count <= 0) { window.location.reload(); return; }
    if (!newBody) return;
    var pageItems = page.querySelector('.cart-page__items');
    var newItems = newBody.querySelector('.cart-drawer__items');
    if (pageItems && newItems) pageItems.innerHTML = newItems.innerHTML;
    var pageTotals = page.querySelector('.cart-page__totals');
    var newTotals = newBody.querySelector('.cart-drawer__totals');
    if (pageTotals && newTotals) pageTotals.innerHTML = newTotals.outerHTML;
  };

  CartDrawerElement.prototype.updateBadges = function (count) {
    // Drawer title pill: blank at 0 so the `:empty` rule hides it.
    var pillText = count > 0 ? String(count) : '';

    // Legacy header badge + text link (sections/header.liquid)
    var headerCount = document.getElementById('cart_item_count');
    if (headerCount) headerCount.textContent = count;
    var textLink = headerCount && headerCount.parentNode
      ? headerCount.parentNode.querySelector('.text-link') : null;
    if (textLink) {
      var label = (window.theme && window.theme.strings && window.theme.strings.cart) || 'Cart';
      textLink.textContent = count > 0 ? label + ' (' + count + ')' : label;
    }

    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = pillText;
    });
  };

  CartDrawerElement.prototype.triggerCurrencyConverter = function () {
    if (window.PXUTheme && window.PXUTheme.currencyConverter &&
        typeof window.PXUTheme.currencyConverter.convertCurrencies === 'function') {
      try { window.PXUTheme.currencyConverter.convertCurrencies(); } catch (e) {}
    }
  };

  CartDrawerElement.prototype.announce = function (action, title) {
    var el = this.body ? this.body.querySelector('[data-cart-status]') : null;
    if (!el) return;
    var msg = '';
    if (action === 'add') msg = title ? title + ' added to cart' : 'Item added to cart';
    else if (action === 'remove') msg = title ? title + ' removed from cart' : 'Item removed from cart';
    else if (action === 'update') msg = title ? 'Quantity updated for ' + title : 'Cart updated';
    el.textContent = msg;
  };

  CartDrawerElement.prototype.pushAnalytics = function (action, title) {
    if (!window.dataLayer) return;
    if (action === 'add') {
      window.dataLayer.push({ event: 'add_to_cart', ecommerce: { items: [{ item_name: title }] } });
    } else if (action === 'remove') {
      window.dataLayer.push({ event: 'remove_from_cart', ecommerce: { items: [{ item_name: title }] } });
    }
  };

  // ----- Recommendations (buffered into the render; never pops in) -----

  CartDrawerElement.prototype.recsEnabled = function () {
    return this.getAttribute('data-recommendations-enabled') === 'true';
  };

  // Best-known seed product id right now (PDP product, else first live cart item).
  CartDrawerElement.prototype.currentSeedId = function () {
    if (document.body.classList.contains('template-product') &&
        window.meta && window.meta.product && window.meta.product.id) {
      return String(window.meta.product.id);
    }
    var item = this.querySelector('.cart-item[data-product-id]');
    return item ? item.getAttribute('data-product-id') : null;
  };

  // Start (or reuse) the recs fetch for a seed. Returns Promise<products[]>.
  // Caches the latest in-flight fetch by seed so it can overlap the cart
  // mutation and isn't re-issued within the same render.
  CartDrawerElement.prototype.prefetchRecs = function (seedId) {
    if (!this.recsEnabled() || !seedId) return Promise.resolve([]);
    seedId = String(seedId);
    if (this._recs && this._recs.seedId === seedId) return this._recs.promise;
    var url = root + 'recommendations/products.json?product_id=' + seedId + '&limit=10&intent=related';
    var promise = fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (payload) { return (payload && payload.products) || []; })
      .catch(function (err) { console.error('Recommendations error:', err); return []; });
    this._recs = { seedId: seedId, promise: promise };
    return promise;
  };

  // Pure builder: recs HTML for `products`, excluding items already in `bodyRoot`.
  CartDrawerElement.prototype.buildRecsHTML = function (products, bodyRoot, heading) {
    var inCart = {};
    Array.prototype.forEach.call(bodyRoot.querySelectorAll('.cart-item[data-variant-id]'), function (el) {
      inCart[el.dataset.variantId] = true;
    });
    var cards = [];
    for (var i = 0; i < products.length && cards.length < 3; i++) {
      var p = products[i];
      if (p.tags && p.tags.indexOf('product_hidden') !== -1) continue;
      var variant = null;
      for (var j = 0; j < p.variants.length; j++) {
        if (p.variants[j].available) { variant = p.variants[j]; break; }
      }
      if (!variant || inCart[variant.id]) continue;
      cards.push(
        '<div class="cart-rec">' +
          '<a href="' + p.url + '" class="cart-rec__image">' +
            (p.featured_image ? '<img src="' + p.featured_image + '" alt="" width="120" height="120" loading="lazy">' : '') +
          '</a>' +
          '<div class="cart-rec__info">' +
            '<a href="' + p.url + '" class="cart-rec__title">' + escapeHTML(p.title) + '</a>' +
            '<span class="cart-rec__price money">' + formatMoney(variant.price) + '</span>' +
          '</div>' +
          '<button type="button" class="cart-rec__add" data-action="add-rec" data-variant-id="' + variant.id + '" aria-label="Add ' + escapeAttr(p.title) + ' to cart">Add</button>' +
        '</div>'
      );
    }
    if (!cards.length) return '';
    return '<p class="cart-drawer__recs-heading">' + escapeHTML(heading) + '</p>' +
           '<div class="cart-drawer__recs-track">' + cards.join('') + '</div>';
  };

  // No-mutation path (first open): fill the live recs slot if it's still empty.
  CartDrawerElement.prototype.ensureRecsLoaded = function () {
    var self = this;
    if (!this.recsEnabled()) return;
    var slot = this.body ? this.body.querySelector('.cart-drawer__recs') : null;
    if (!slot || slot.innerHTML) return;
    var seedId = this.currentSeedId();
    if (!seedId) return;
    var heading = slot.dataset.recsHeading || 'Your Hair might like this too';
    this.prefetchRecs(seedId).then(function (products) {
      if (products && products.length && slot && !slot.innerHTML) {
        slot.innerHTML = self.buildRecsHTML(products, self, heading);
      }
    });
  };

  // ----- Compatibility shim: window.liquidAjaxCart -----
  // Keeps existing callers (theme.js, hue, subscription-modal, bundle-builder)
  // working without edits.

  CartDrawerElement.prototype.setupShim = function () {
    var self = this;
    window.liquidAjaxCart = window.liquidAjaxCart || {};

    window.liquidAjaxCart.cartRequestUpdate = function () {
      self.setBusy(true);
      self.prefetchRecs(self.currentSeedId());
      self.enqueue(function () {
        return CartAPI.refresh().then(function (data) {
          return self.renderFromResponse(data, 'update', '');
        }).catch(function (err) {
          console.error('cartRequestUpdate error:', err);
          self.setBusy(false);
        });
      });
    };

    window.liquidAjaxCart.cartRequestAdd = function (payload, opts) {
      opts = opts || {};
      self.setBusy(true);
      self.prefetchRecs(self.currentSeedId());
      self.enqueue(function () {
        return CartAPI.add(payload).then(function (data) {
          return self.renderFromResponse(data, 'add', '').then(function () {
            self.open();
            if (typeof opts.lastComplete === 'function') {
              opts.lastComplete({ responseData: { ok: true, body: data } });
            }
          });
        }).catch(function (err) {
          console.error('cartRequestAdd error:', err);
          if (typeof opts.lastComplete === 'function') {
            opts.lastComplete({ responseData: { ok: false } });
          }
        });
      });
    };

    window.liquidAjaxCart._openAndUpdate = function () {
      // Buffered: request the open, refresh, and let the render flush it.
      self._openRequested = true;
      window.liquidAjaxCart.cartRequestUpdate();
    };
  };

  // ----- helpers -----

  function parseRgb(str) {
    var m = str && str.match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    var p = m[1].split(',').map(function (x) { return parseFloat(x); });
    var a = p.length > 3 ? p[3] : 1;
    return a > 0.1 ? [p[0], p[1], p[2]] : null;
  }

  // The button's RESTING background color, immune to the current :hover state.
  // Theme buttons invert background<->text on hover, and the browser keeps :hover
  // while the cursor is over the button — so reading the live computed bg at click
  // time returns the wrong (hover) color. We read an off-screen clone (same DOM
  // context, but not under the cursor) to get the true resting background.
  function restingBg(el) {
    var rgb = null;
    try {
      var clone = el.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.remove('atc-loading');
      clone.style.cssText += ';position:absolute !important;left:-9999px !important;top:0 !important;pointer-events:none !important;transition:none !important;';
      el.parentNode.insertBefore(clone, el.nextSibling);
      rgb = parseRgb(getComputedStyle(clone).backgroundColor);
      clone.parentNode.removeChild(clone);
    } catch (e) {}
    if (rgb) return rgb;
    // Fallback: walk up the original's ancestors for the first solid background.
    var n = el.parentElement;
    while (n && n.nodeType === 1) {
      var c = parseRgb(getComputedStyle(n).backgroundColor);
      if (c) return c;
      n = n.parentElement;
    }
    return null;
  }

  function escapeHTML(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(str) {
    return escapeHTML(str).replace(/"/g, '&quot;');
  }
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Define once
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawerElement);
  }
})();
